// src/reports/ReportsListView.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  Chip,
  Fade,
  useTheme,
} from '@mui/material';
import {
  FaSearch,
  FaFilter,
  FaSort,
  FaList,
  FaTh,
  FaChevronDown,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import ReportListItem from './ReportListItem';

export default function ReportsListView({ reports = [], loading = false }) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  if (!Array.isArray(reports) || reports.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: '16px',
          bgcolor: 'rgba(245,250,246,0.8)',
          border: '2px dashed rgba(46,125,50,0.2)',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          📋 No Reports Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are currently no reports to display. Check back later!
        </Typography>
      </Paper>
    );
  }

  // Filter reports
  const filteredReports = reports.filter((report) => {
    const matchesSearch = report.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || report.status?.toLowerCase() === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort reports
  const sortedReports = [...filteredReports].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.timestamp) - new Date(a.timestamp);
    } else if (sortBy === 'oldest') {
      return new Date(a.timestamp) - new Date(b.timestamp);
    } else if (sortBy === 'status') {
      return a.status?.localeCompare(b.status);
    } else if (sortBy === 'category') {
      return a.category?.localeCompare(b.category);
    }
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedReports.length / itemsPerPage);
  const paginatedReports = sortedReports.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'verified', label: 'Verified' },
    { value: 'spammed', label: 'spammed' },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'status', label: 'By Status' },
    { value: 'category', label: 'By Category' },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header */}

      {/* Filters Bar */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '12px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          bgcolor: 'rgba(245, 250, 246, 0.82)',
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Search reports..."
          value={searchTerm}
          type='search'
          onChange={handleSearchChange}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch size={16} color="#666" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={filterStatus}
            onChange={handleFilterChange}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <FaFilter size={14} color="#666" />
              </InputAdornment>
            }
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            displayEmpty
            startAdornment={
              <InputAdornment position="start">
                <FaSort size={14} color="#666" />
              </InputAdornment>
            }
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Results count */}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {sortedReports.length} report{sortedReports.length !== 1 ? 's' : ''} found
        </Typography>
      </Paper>

      {/* Reports List */}
      <AnimatePresence>
        <Box>
          {paginatedReports.map((report, index) => (
            <motion.div
              key={report.report_id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <ReportListItem report={report} index={index} />
            </motion.div>
          ))}
        </Box>
      </AnimatePresence>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                '&.Mui-selected': {
                  bgcolor: '#2e7d32',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#1b5e20',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}